import React, { useState, useCallback } from "react";
import { View } from "react-native";
import { AppText } from "@/components/ui/AppText";
import { Button } from "@/components/ui/Button";
import { Modal as UIModal } from "@/components/ui/Modal";
import { AppTextArea } from "@/components/ui/AppTextArea";
import { SuccessState } from "@/components/ui/SuccessState";
import { ErrorState } from "@/components/ui/ErrorState";
import type { TranslationKeys } from "@chinese-vendor/i18n";

export interface RejectConfirmModalProps {
  visible: boolean;
  onDismiss: () => void;
  onConfirm: () => void;
  vendorName?: string;
  isSubmitting?: boolean;
  onSubmit: (reason: string) => Promise<void>;
  t: ReturnType<typeof import("@/context/I18nContext").useI18n>["t"];
}

type RejectionState = "form" | "success" | "error";

export function RejectConfirmModal({
  visible,
  onDismiss,
  onConfirm,
  vendorName,
  isSubmitting,
  onSubmit,
  t,
}: RejectConfirmModalProps) {
  const [reason, setReason] = useState("");
  const [state, setState] = useState<RejectionState>("form");
  const [error, setError] = useState<TranslationKeys | null>(null);

  const resetForm = useCallback(() => {
    setState("form");
    setReason("");
    setError(null);
  }, []);

  const handleDismiss = useCallback(() => {
    resetForm();
    onDismiss();
    if (state === "success") {
      onConfirm();
    }
  }, [resetForm, onDismiss, onConfirm, state]);

  const handleConfirm = useCallback(async () => {
    setError(null);
    try {
      await onSubmit(reason.trim());
      setState("success");
    } catch {
      setError("common.error.generic");
      setState("error");
    }
  }, [onSubmit, reason]);

  return (
    <UIModal
      visible={visible}
      onDismiss={handleDismiss}
      title={state === "form" ? "review.review.decision.reject" : null}
      closeOnBackdrop={false}
      useScrollView
      footer={
        state === "form" ? (
          <View className="flex-row items-center justify-end gap-4">
            <Button label="common.cancel" variant="tertiary" onPress={handleDismiss} />
            <Button
              label="review.review.decision.rejectModal.submitRejection"
              variant="destructive"
              disabled={!reason.trim() || isSubmitting}
              loading={isSubmitting}
              onPress={handleConfirm}
            />
          </View>
        ) : null
      }
    >
      {state === "form" && (
        <>
          {/* Vendor banner */}
          {vendorName && (
            <View className="bg-error-light border border-error-border rounded-lg px-6 py-5 mb-6 gap-2 items-start">
              <AppText weight="semibold" className="text-sm text-error tracking-[0.56px]">
                {t("review.review.decision.rejectModal.rejectedVendor").toUpperCase()}
              </AppText>
              <AppText weight="medium" className="text-lg text-dark-text-50">
                {vendorName}
              </AppText>
            </View>
          )}

          {/* Reason textarea */}
          <AppTextArea
            label="review.review.decision.rejectModal.reasonForRejection"
            placeholder="review.review.decision.rejectModal.reasonPlaceholder"
            value={reason}
            onChangeText={setReason}
            numberOfLines={4}
            required
          />
        </>
      )}

      {state === "success" && (
        <SuccessState
          title="review.review.decision.rejectModal.successTitle"
          description="review.review.decision.rejectModal.successDescription"
          className="px-0 pt-0"
        />
      )}

      {state === "error" && (
        <ErrorState
          description={error || "common.error.generic"}
          className="px-0 pt-0"
          onRetry={() => {
            setState("form");
            setError(null);
          }}
        />
      )}
    </UIModal>
  );
}
